import { useInterval, useMemoizedFn } from 'ahooks'
import { initializeApp } from 'firebase/app'
import { getAuth, signInAnonymously } from 'firebase/auth'
import { jwtDecode } from 'jwt-decode'
import type Keycloak from 'keycloak-js'
import { createContext, useContext, useRef, useState } from 'react'
import { autoLogoutTime } from '../constants/autoLogout'
import { autoTabCloseTime } from '../constants/autoTabClose'
import { useApiUrl } from './useApiUrl'
import { loadStatus } from '../constants/loadStatus'
import { errorMsg } from '../constants/messages'
import { firebaseConfig } from '../gcp-config/firebaseConfig'
import type { Result } from '../interfaces/Result'
import { useC001IdTokenGet } from './useC001IdTokenGet'

export const useAuth = () => {
  return useContext(authContext)
}
const firebaseApp = initializeApp(firebaseConfig)

interface UseAuth {
  isAuthenticated: boolean
  isAutoLogout: boolean
  isAutoTabClose: boolean
  isAnonymous: boolean
  // jwt: string;
  tenant: string
  loginId: string
  deviceId: string
  convId: string
  role: number
  rhq: string
  salesCompany: string
  loading: loadStatus
  signIn: (loginId: string, password: string) => Promise<Result>
  signOut: () => void

  setIsAutoLogout: (isAutoLogout: boolean) => void
  setIsAutoTabClose: (isAutoTabClose: boolean) => void
  setRole: (role: number) => void
  setRhq: (rhq: string) => void
  setSalesCompany: (salesCompany: string) => void
  setTenant: (tenant: string) => void
  setDeviceId: (deviceId: string) => void
  // sendEmailVerification: (email: string, reset: number) => Promise<Result>;
  // signInEmailLink: (email: string) => Promise<ResultEmailLinkInfo>;
  // sendPasswordReset: (email: string) => Promise<Result>;
  // passwordReset: (code: string, newPassword: string) => Promise<Result>;
  // signInWithEmail: (email: string, password: string) => Promise<Result>;
  // signInWithToken: (token: string) => Promise<Result>;
  signInAnonymous: (convId: string) => Promise<Result>
  signInWithKeycloak: (kc: Keycloak) => void
  authSource: 'firebase' | 'keycloak' | null
  handleActivity: () => void
  getJwt: () => Promise<string>
  isDisable: boolean
  setIsDisable: (is: boolean) => void
}

const authContext = createContext({} as UseAuth)

type Props = {
  children: React.ReactNode
}

// 認証プロバイダ
export const ProvideAuth: React.FC<Props> = ({ children }) => {
  const auth = useProvideAuth()
  return <authContext.Provider value={auth}>{children}</authContext.Provider>
}

const useProvideAuth = (): UseAuth => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isAnonymous, setIsAnonymous] = useState(false)
  const [isDisable, setIsDisable] = useState(false)
  const [convId, setConvId] = useState('')
  const [loginId, setLoginId] = useState('')
  const [password, setPassword] = useState('')
  const [tenant, setTenant] = useState('')
  const [deviceId, setDeviceIdState] = useState(() => sessionStorage.getItem('deviceId') ?? '')
  const setDeviceId = (id: string) => {
    sessionStorage.setItem('deviceId', id)
    setDeviceIdState(id)
  }
  const [jwt, setJwt] = useState('')
  const [role, setRole] = useState(0)
  const [rhq, setRhqState] = useState(() => sessionStorage.getItem('rhq') ?? '')
  const setRhq = (value: string) => {
    sessionStorage.setItem('rhq', value)
    setRhqState(value)
  }
  const [salesCompany, setSalesCompanyState] = useState(
    () => sessionStorage.getItem('salesCompany') ?? '',
  )
  const setSalesCompany = (value: string) => {
    sessionStorage.setItem('salesCompany', value)
    setSalesCompanyState(value)
  }
  const lastActivityRef = useRef<number>(Date.now())
  const [isAutoLogout, setIsAutoLogout] = useState(false)
  const [isAutoTabClose, setIsAutoTabClose] = useState(false)
  const [loading, setLoading] = useState(loadStatus.NONE)
  const [authSource, setAuthSource] = useState<'firebase' | 'keycloak' | null>(null)
  const keycloakRef = useRef<Keycloak | null>(null)

  const c001 = useC001IdTokenGet()

  const firebaseAuth = getAuth(firebaseApp)

  const apiUrl = useApiUrl()

  const handleAutoLogoutOrTabClose = useMemoizedFn(() => {
    if (apiUrl.isDcp) {
      // DCP環境：自動タブクローズのみ有効
      setIsAutoTabClose(true)
    } else {
      // 非DCP環境：自動ログアウトのみ有効
      setIsAutoLogout(true)
    }
    lastActivityRef.current = 0
  })

  useInterval(
    () => {
      const timeoutThreshold = apiUrl.isDcp ? autoTabCloseTime : autoLogoutTime
      if (Date.now() - lastActivityRef.current > timeoutThreshold && isAuthenticated) {
        handleAutoLogoutOrTabClose()
      }
    },
    10000, // 10秒ごとにチェック
  )

  const handleActivity = useMemoizedFn(() => {
    // アクティビティがあったことを記録
    lastActivityRef.current = Date.now()
  })

  const signIn = async (loginId: string, password: string) => {
    try {
      setLoading(loadStatus.LOADING)
      // IDトークン取得
      const result = await c001.execIdTokenGet(loginId, password, undefined)
      if (result.success) {
        // console.log(result.message);
        setLoginId(loginId)
        setPassword(password)
        setJwt(result.message!)
        setIsAuthenticated(true)
        setIsAnonymous(false)
      }
      setLoading(loadStatus.LOADED)
      return result
    } catch (error) {
      // console.log(error);
      setLoading(loadStatus.LOADED)
      return { success: false, message: errorMsg.SYSTEM_ERROR }
    }
  }
  const signOut = async () => {
    if (authSource === 'keycloak' && keycloakRef.current) {
      const idToken = keycloakRef.current.idToken
      const tenant = window.location.hostname.split('.')[0]
      const redirectUri = encodeURIComponent(`${window.location.origin}/sso`)
      let logoutUrl = `${process.env.REACT_APP_KEYCLOAK_URL}/realms/${tenant}/protocol/openid-connect/logout`
        + `?post_logout_redirect_uri=${redirectUri}`
      if (idToken) {
        logoutUrl += `&id_token_hint=${encodeURIComponent(idToken)}`
      }
      window.location.href = logoutUrl
      return { success: true }
    }
    setLoginId('')
    setJwt('')
    setIsAuthenticated(false)
    setAuthSource(null)
    keycloakRef.current = null
    return {
      success: true,
    }
  }

  // const sendEmailVerification = async (email: string, reset: number) => {
  //   try {
  //     // reset off:0 , reset on:1
  //     const callbackUrl = `https://${process.env.REACT_APP_BASE_URL}/index.html?email=${email}&reset=${reset}`;
  //     const actionCodeSettings = {
  //       url: callbackUrl,
  //       handleCodeInApp: true,
  //     };
  //     // 認証メール送信
  //     await sendSignInLinkToEmail(firebaseAuth, email, actionCodeSettings);

  //     // サインイン成功時の処理
  //     // console.log("sendEmailVerification");
  //     return { success: true };
  //   } catch (error) {
  //     console.error("Error signing in with email link:", error);
  //     return { success: false, message: errorMsg.SYSTEM_ERROR };
  //     // エラー処理
  //   }
  // };
  // const signInEmailLink = async (email: string) => {
  //   setLoading(loadStatus.LOADING);
  //   if (await isSignInWithEmailLink(firebaseAuth, window.location.href)) {
  //     try {
  //       const result = await signInWithEmailLink(
  //         firebaseAuth,
  //         email,
  //         window.location.href
  //       );
  //       setLoginId(result.user.uid);
  //       const jwt = await result.user.getIdToken();
  //       setJwt(await result.user.getIdToken());

  //       // console.log("signInWithEmailLink successed.");
  //       setLoading(loadStatus.LOADED);
  //       return { success: true, info: { login_id: result.user.uid, jwt: jwt } };
  //     } catch (error) {
  //       // console.log(error);
  //       // メールリンク認証済みの場合
  //       try {
  //         const user = firebaseAuth.currentUser;
  //         if (user) {
  //           if (user.emailVerified) {
  //             setLoginId(user.uid);
  //             const jwt = await user.getIdToken();
  //             setJwt(await user.getIdToken());
  //             // console.log("signInWithEmailLink skip.");
  //             setLoading(loadStatus.LOADED);
  //             return {
  //               success: true,
  //               info: { login_id: user.uid, jwt: jwt },
  //             };
  //           } else {
  //             setLoading(loadStatus.LOADED);
  //             return { success: false, message: errorMsg.AUTH_ERROR };
  //           }
  //         }
  //       } catch (error) {
  //         // console.log(error);
  //       }
  //       setLoading(loadStatus.LOADED);
  //       return { success: false, message: errorMsg.AUTH_ERROR };
  //     }
  //   }
  //   setLoading(loadStatus.LOADED);
  //   return { success: false, message: errorMsg.AUTH_ERROR };
  // };
  // const sendPasswordReset = async (email: string) => {
  //   try {
  //     setLoading(loadStatus.LOADING);
  //     const callbackUrl = `https://${process.env.REACT_APP_BASE_URL}/index.html`;
  //     const actionCodeSettings = {
  //       url: callbackUrl,
  //       handleCodeInApp: true,
  //     };
  //     // パスワードリセットメール送信
  //     await sendPasswordResetEmail(firebaseAuth, email, actionCodeSettings);

  //     // console.log("Password Reset mail successful");
  //     setLoading(loadStatus.LOADED);
  //     return { success: true };
  //   } catch (error) {
  //     console.error("Error signing in with email link:", error);
  //     setLoading(loadStatus.LOADED);
  //     return { success: false, message: errorMsg.SYSTEM_ERROR };
  //     // エラー処理
  //   }
  // };
  // const passwordReset = async (code: string, newPassword: string) => {
  //   try {
  //     setLoading(loadStatus.LOADING);
  //     await confirmPasswordReset(firebaseAuth, code, newPassword);
  //     // console.log("signInWithEmailLink successed.");
  //     setLoading(loadStatus.LOADED);
  //     return { success: true };
  //   } catch (error) {
  //     // console.log(error);
  //     setLoading(loadStatus.LOADED);
  //     return { success: false, message: errorMsg.SYSTEM_ERROR };
  //   }
  // };
  // const signInWithEmail = async (email: string, password: string) => {
  //   // 未使用
  //   try {
  //     setLoading(loadStatus.LOADING);
  //     const result = await signInWithEmailAndPassword(
  //       firebaseAuth,
  //       email,
  //       password
  //     );
  //     // console.log("signInWithEmailAndPassword successed.");
  //     setLoginId(result.user.uid);
  //     setLoading(loadStatus.LOADED);
  //     return { success: true, message: result.user.uid };
  //   } catch (error) {
  //     // console.log(error);
  //     setLoading(loadStatus.LOADED);
  //     return { success: false, message: errorMsg.SYSTEM_ERROR };
  //   }
  // };
  // const signInWithToken = async (token: string) => {
  //   try {
  //     setLoading(loadStatus.LOADING);
  //     const result = await signInWithCustomToken(firebaseAuth, token);
  //     // console.log("signInWithCustomToken successed.");
  //     const idToken = await result.user.getIdToken();
  //     setJwt(idToken);
  //     // console.log(idToken);
  //     setLoading(loadStatus.LOADED);
  //     return { success: true, message: idToken };
  //   } catch (error) {
  //     // console.log(error);
  //     setLoading(loadStatus.LOADED);
  //     return { success: false, message: errorMsg.SYSTEM_ERROR };
  //   }
  // };
  const signInAnonymous = async (convId: string) => {
    try {
      setLoading(loadStatus.LOADING)
      const result = await signInAnonymously(firebaseAuth)
      // console.log("signInAnonymously successed.");
      setConvId(convId)
      setIsAuthenticated(true)
      setIsAnonymous(true)
      const idToken = await result.user.getIdToken()
      setJwt(idToken)
      // console.log(idToken);
      setLoading(loadStatus.LOADED)
      return { success: true, message: idToken }
    } catch (error) {
      // console.log(error);
      setLoading(loadStatus.LOADED)
      return { success: false, message: errorMsg.SYSTEM_ERROR }
    }
  }
  const signInWithKeycloak = (kc: Keycloak) => {
    keycloakRef.current = kc
    setAuthSource('keycloak')
    setJwt(kc.token!)
    setIsAuthenticated(true)
    setIsAnonymous(false)

    const parsed = kc.tokenParsed!
    setLoginId(parsed.preferred_username || parsed.sub || '')

    const azp = parsed.azp || ''
    if (azp === 'provider-portal') {
      setRole(2)
    } else if (azp === 'customer-portal') {
      setRole(1)
    } else {
      setRole(1)
    }
  }

  const isTokenExpired = (token: string) => {
    const decodedToken = jwtDecode(token)

    if (decodedToken.exp) {
      const currentTime = Date.now().valueOf() / 1000

      if (decodedToken.exp < currentTime) {
        console.log('token expired.')
        return true
      }
      // console.log("token ok.");
      return false
    }
    // console.log("token invalid");
    return false
  }
  const getJwt = async () => {
    // Keycloak token refresh
    if (authSource === 'keycloak' && keycloakRef.current) {
      try {
        await keycloakRef.current.updateToken(60)
        const newToken = keycloakRef.current.token!
        setJwt(newToken)
        return newToken
      } catch {
        keycloakRef.current.login()
        return ''
      }
    }

    // Firebase token refresh
    let token = jwt
    if (jwt) {
      if (isTokenExpired(jwt)) {
        // トークンの有効期限が切れている場合リフレッシュトークンを払い出す。
        // IDトークン取得
        const result = await c001.execIdTokenGet(loginId, password, undefined)
        if (result.success) {
          setJwt(result.message!)
          token = result.message!
        }
      }
    }
    return token
  }
  return {
    isAuthenticated,
    isAutoLogout,
    isAutoTabClose,
    isAnonymous,
    // jwt,
    loginId,
    tenant,
    deviceId,
    convId,
    role,
    rhq,
    salesCompany,
    loading,
    signIn,
    signOut,
    // getJwt,
    setIsAutoLogout,
    setIsAutoTabClose,
    setRole,
    setRhq,
    setSalesCompany,
    setTenant,
    setDeviceId,
    // sendEmailVerification,
    // signInEmailLink,
    // sendPasswordReset,
    // passwordReset,
    // signInWithEmail,
    // signInWithToken,
    signInAnonymous,
    signInWithKeycloak,
    authSource,
    handleActivity,
    getJwt,
    isDisable,
    setIsDisable,
  }
}
